/*
 * Artich.io — Défilement infini des collections (thème Prestige)
 * Aucun fichier du thème n'est modifié : on suit le lien de pagination natif
 * (il conserve filtres et tri) et on ajoute les cartes produit à la grille.
 * Sans JS ou en cas d'erreur, la pagination numérotée reste disponible.
 */
class ArtichInfiniteScroll extends HTMLElement {
  connectedCallback() {
    // Distance avant la fin de la grille à partir de laquelle on précharge (réglable via data-root-margin)
    this.margin = this.dataset.rootMargin || '800px';
    this.bind();

    // Les filtres Prestige remplacent la grille en Ajax : on se rebranche quand elle change
    this.mutationObserver = new MutationObserver(() => {
      const list = document.querySelector('product-list');
      if (list && list !== this.list) this.bind();
    });
    this.mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.intersectionObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.sentinel?.remove();
  }

  bind() {
    this.intersectionObserver?.disconnect();
    this.sentinel?.remove();
    this.loading = false;

    this.list = document.querySelector('product-list');
    this.pagination = document.querySelector('nav.pagination');
    this.lastPage = Number(new URL(window.location.href).searchParams.get('page')) || 1;
    if (!this.list || !this.pagination || !this.nextUrl()) return;

    this.sentinel = document.createElement('div');
    this.sentinel.className = 'artich-infinite-scroll__sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');
    // Le repère vit dans la grille produits (pleine largeur) : le conteneur parent est lui-même une grille CSS
    this.sentinel.style.gridColumn = '1 / -1';
    this.list.appendChild(this.sentinel);
    this.pagination.classList.add('artich-infinite-scroll__pagination');

    this.intersectionObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
    }, { rootMargin: `0px 0px ${this.margin} 0px` });
    this.intersectionObserver.observe(this.sentinel);
  }

  nextUrl() {
    const link = [...this.pagination.querySelectorAll('a[href]')].find(
      (a) => Number(new URL(a.href, window.location.origin).searchParams.get('page')) === this.lastPage + 1
    );
    return link ? link.href : null;
  }

  async loadNext() {
    if (this.loading) return;
    const url = this.nextUrl();
    if (!url) return this.finish();

    this.loading = true;
    this.sentinel.classList.add('is-loading');
    this.list.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const cards = doc.querySelectorAll('product-list product-card');
      if (!cards.length) return this.finish();

      const fragment = document.createDocumentFragment();
      cards.forEach((card) => {
        const node = document.importNode(card, true);
        // Prestige masque les cartes [reveal-on-scroll] (opacity: 0) jusqu'à son animation d'entrée,
        // qui ne tourne qu'au chargement de la page : on retire l'attribut pour les cartes ajoutées
        node.removeAttribute('reveal-on-scroll');
        node.querySelectorAll('[reveal-on-scroll]').forEach((el) => el.removeAttribute('reveal-on-scroll'));
        fragment.appendChild(node);
      });
      this.list.insertBefore(fragment, this.sentinel);

      // La pagination de la page chargée indique s'il reste une page suivante
      this.lastPage += 1;
      const nextPagination = doc.querySelector('nav.pagination');
      if (nextPagination) this.pagination.innerHTML = nextPagination.innerHTML;
      if (!this.nextUrl()) this.finish();
    } catch (error) {
      // Échec réseau : on rend la pagination numérotée au client
      this.finish(true);
    } finally {
      this.loading = false;
      this.sentinel?.classList.remove('is-loading');
      this.list?.removeAttribute('aria-busy');
      // Réarme l'observateur : si le repère est encore visible, la page suivante se charge aussi
      if (this.sentinel?.isConnected) {
        this.intersectionObserver?.unobserve(this.sentinel);
        this.intersectionObserver?.observe(this.sentinel);
      }
    }
  }

  finish(showPagination = false) {
    this.intersectionObserver?.disconnect();
    this.sentinel?.remove();
    if (showPagination) this.pagination?.classList.remove('artich-infinite-scroll__pagination');
  }
}

if (!customElements.get('artich-infinite-scroll')) {
  customElements.define('artich-infinite-scroll', ArtichInfiniteScroll);
}
